import { useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import useDesignTokens from '../../../../themes/useDesignTokens';
import useCommonButtonStyles from './useCommonButtonStyles';

interface AssetronButtonStylesProps {
  size: string;
  hierarchy: string;
  destructive: boolean;
  color?: string;
  showNotAllowedCursor?: boolean;
}

const useAssetronButtonStyles = ({
  size,
  hierarchy,
  destructive,
  color,
  showNotAllowedCursor,
}: AssetronButtonStylesProps) => {
  const theme = useTheme();
  const { common } = theme.palette;

  const tokens = useDesignTokens();
  const { link, primary, primaryColor, secondary, tertiary } =
    tokens.components.button;

  const {
    baseButtonStyles,
    getProgressColor,
    getDisabledStyles,
    baseIconStyles,
    getDisabledIconStyles,
  } = useCommonButtonStyles({
    hierarchy,
    size,
  });

  const getPrimaryHierarchyStyles = useCallback(() => {
    return {
      backgroundColor: destructive
        ? primary.error.bg.default
        : primary.default.bg.default,
      color: `${common.white} !important`,
      border: `1px solid ${destructive ? primary.error.border.default : primary.default.border.default}`,
      '&:hover': {
        backgroundColor: destructive
          ? primary.error.bg.hover
          : primary.default.bg.hover,
        color: common.white,
        border: `1px solid ${destructive ? primary.error.border.hover : primary.default.border.hover}`,
        cursor: showNotAllowedCursor ? 'not-allowed' : 'pointer',
      },
      '&:active': {
        backgroundColor: destructive
          ? primary.error.bg.active
          : primary.default.bg.active,
        color: common.white,
        border: `1px solid ${destructive ? primary.error.border.active : primary.default.border.active}`,
      },
    };
  }, [common, destructive, showNotAllowedCursor, primary]);

  const getPrimaryColorHierarchyStyles = useCallback(() => {
    return {
      backgroundColor: destructive
        ? primaryColor.error.bg.default
        : primaryColor.default.bg.default,
      color: `${common.white} !important`,
      border: `1px solid ${destructive ? primaryColor.error.border.default : primaryColor.default.border.default}`,
      '&:hover': {
        backgroundColor: destructive
          ? primaryColor.error.bg.hover
          : primaryColor.default.bg.hover,
        color: common.white,
        border: `1px solid ${destructive ? primaryColor.error.border.hover : primaryColor.default.border.hover}`,
      },
      '&:active': {
        backgroundColor: destructive
          ? primaryColor.error.bg.active
          : primaryColor.default.bg.active,
        color: common.white,
        border: `1px solid ${destructive ? primaryColor.error.border.active : primaryColor.default.border.active}`,
      },
    };
  }, [common, destructive, primaryColor]);

  const getSecondaryHierarchyStyles = useCallback(() => {
    return {
      backgroundColor: destructive
        ? secondary.error.bg.default
        : secondary.default.bg.default,
      border: `1px solid ${destructive ? secondary.error.border.default : secondary.default.border.default}`,
      color: destructive
        ? secondary.error.fg.default
        : secondary.default.fg.default,
      '&:hover': {
        color: destructive
          ? secondary.error.fg.hover
          : secondary.default.fg.hover,
        backgroundColor: destructive
          ? secondary.error.bg.hover
          : secondary.default.bg.hover,
        border: `1px solid ${destructive ? secondary.error.border.hover : secondary.default.border.hover}`,
        cursor: showNotAllowedCursor ? 'not-allowed' : 'pointer',
      },
      '&:active': {
        backgroundColor: destructive
          ? secondary.error.bg.active
          : secondary.default.bg.active,
        border: `1px solid ${destructive ? secondary.error.border.active : secondary.default.border.active}`,
        color: destructive
          ? secondary.error.fg.active
          : secondary.default.fg.active,
      },
    };
  }, [destructive, showNotAllowedCursor, secondary]);

  const getSecondaryColorHierarchyStyles = useCallback(() => {
    return {
      backgroundColor: destructive
        ? secondary.error.bg.default
        : secondary.default.bg.default,
      border: `1px solid ${destructive ? secondary.error.border.default : secondary.default.border.color.default}`,
      boxShadow: '0px 1px 2px 0px rgba(12, 33, 70, 0.05)',
      color: destructive
        ? secondary.error.fg.default
        : secondary.default.fg.color.default,
      '&:hover': {
        color: destructive
          ? secondary.error.fg.hover
          : secondary.default.fg.color.hover,
        backgroundColor: destructive
          ? secondary.error.bg.hover
          : secondary.default.bg.color.hover,
        border: `1px solid ${destructive ? secondary.error.border.hover : secondary.default.border.color.hover}`,
        boxShadow: '0px 1px 2px 0px rgba(12, 33, 70, 0.05)',
      },
      '&:active': {
        backgroundColor: destructive
          ? secondary.error.bg.active
          : secondary.default.bg.color.active,
        border: `1px solid ${destructive ? secondary.error.border.active : secondary.default.border.color.active}`,
        color: destructive
          ? secondary.error.fg.active
          : secondary.default.fg.color.active,
        boxShadow: '0px 1px 2px 0px rgba(12, 33, 70, 0.05)',
      },
    };
  }, [destructive, secondary]);

  const getTertiaryHierarchyStyles = useCallback(() => {
    return {
      color: destructive
        ? tertiary.error.fg.default
        : tertiary.default.fg.default,

      '&:hover': {
        backgroundColor: destructive
          ? tertiary.error.bg.hover
          : tertiary.default.bg.hover,

        color: destructive
          ? tertiary.error.fg.hover
          : tertiary.default.fg.hover,
      },
      '&:active': {
        backgroundColor: destructive
          ? tertiary.error.bg.active
          : tertiary.default.bg.active,
        color: destructive
          ? tertiary.error.fg.active
          : tertiary.default.fg.active,
      },
    };
  }, [destructive, tertiary]);

  const getTertiaryColorHierarchyStyles = useCallback(() => {
    return {
      backgroundColor: destructive
        ? tertiary.error.bg.default
        : tertiary.default.bg.color.default,
      color: destructive
        ? tertiary.error.fg.default
        : tertiary.default.fg.color.default,
      '&:hover': {
        backgroundColor: destructive
          ? tertiary.error.bg.hover
          : tertiary.default.bg.color.hover,
        color: destructive
          ? tertiary.error.fg.hover
          : tertiary.default.fg.color.hover,
      },
      '&:active': {
        backgroundColor: destructive
          ? tertiary.error.bg.active
          : tertiary.default.bg.color.active,
      },
    };
  }, [destructive, tertiary]);

  const getLinkHierarchyStyles = useCallback(() => {
    return {
      display: 'inline-flex',
      alignItems: 'center',
      height: 'auto',
      width: 'auto',
      fontSize: 'inherit !important',
      padding: theme.sizeGuide.none,
      cursor: 'pointer',
      color: `${destructive ? link.error.fg.color : link.default.fg.default} !important`,
      textDecoration: 'none',
      backgroundColor: 'inherit',
      '&:hover': {
        color: `${destructive ? link.error.fg.hover : link.default.fg.hover} !important`,
        backgroundColor: 'inherit',
        textDecoration: 'underline',
      },
      '&:active': {
        color: `${destructive ? link.error.fg.active : link.default.fg.active} !important`,
        backgroundColor: 'inherit',
      },
      '&::after': {
        content: 'none',
      },
      '&:disabled': {
        ...getDisabledStyles(),
        ':hover': {
          color: `${tokens.colors.text.disabled.primary} !important`,
          textDecoration: 'none',
        },
      },
    };
  }, [destructive, link, theme, tokens, getDisabledStyles]);

  const getLinkColorHierarchyStyles = useCallback(() => {
    return {
      display: 'inline-flex',
      alignItems: 'center',
      height: 'auto',
      width: 'auto',
      minWidth: 'auto',
      fontSize: 'inherit !important',
      padding: theme.sizeGuide.none,
      color: `${destructive ? link.error.fg.color : link.default.fg.color} !important`,
      textDecoration: 'none',
      cursor: 'pointer',
      backgroundColor: 'inherit',
      '&:hover': {
        color: `${destructive ? link.error.fg.hover : link.default.fg.hover} !important`,
        backgroundColor: 'inherit',
        textDecoration: 'underline',
      },
      '&:active': {
        color: `${destructive ? link.error.fg.active : link.default.fg.active} !important`,
        backgroundColor: 'inherit',
      },
      '&::after': {
        content: 'none',
      },
      '&:disabled': {
        ...getDisabledStyles(),
        ':hover': {
          color: `${tokens.colors.text.disabled.primary} !important`,
          textDecoration: 'none',
        },
      },
    };
  }, [destructive, getDisabledStyles, link, theme, tokens]);

  const getCustomColorStyles = useCallback(() => {
    if (!color) return {};

    // For link and link-color hierarchies, maintain their special styling
    if (hierarchy === 'link' || hierarchy === 'link-color') {
      return {
        display: 'inline-flex',
        alignItems: 'center',
        height: 'auto',
        width: 'auto',
        fontSize: 'inherit !important',
        padding: theme.sizeGuide.none,
        cursor: 'pointer',
        color: `${color} !important`,
        textDecoration: 'none',
        backgroundColor: 'inherit',
        '&:hover': {
          color: `${color} !important`,
          backgroundColor: 'inherit',
          textDecoration: 'underline',
          opacity: 0.8,
        },
        '&:active': {
          color: `${color} !important`,
          backgroundColor: 'inherit',
          opacity: 0.9,
        },
        '&::after': {
          content: 'none',
        },
        '&:disabled': {
          ...getDisabledStyles(),
          ':hover': {
            color: `${tokens.colors.text.disabled} !important`,
            textDecoration: 'none',
          },
        },
      };
    }

    // For tertiary hierarchies, maintain transparent background
    if (hierarchy === 'tertiary' || hierarchy === 'tertiary-color') {
      return {
        color: `${color} !important`,
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: color,
          color: `${common.white} !important`,
          opacity: 0.1,
        },
        '&:active': {
          backgroundColor: color,
          color: `${common.white} !important`,
          opacity: 0.2,
        },
      };
    }

    // For primary, secondary, and secondary-color hierarchies
    return {
      backgroundColor: color,
      color: `${common.white} !important`,
      border: `1px solid ${color}`,
      '&:hover': {
        backgroundColor: color,
        color: common.white,
        border: `1px solid ${color}`,
        opacity: 0.8,
      },
      '&:active': {
        backgroundColor: color,
        color: common.white,
        border: `1px solid ${color}`,
        opacity: 0.9,
      },
    };
  }, [
    color,
    common.white,
    hierarchy,
    theme.sizeGuide.none,
    getDisabledStyles,
    tokens.colors.text.disabled,
  ]);

  const hierarchyStyles: { [key: string]: unknown } = useMemo(
    () => ({
      primary: color ? getCustomColorStyles() : getPrimaryHierarchyStyles(),
      'primary-color': color
        ? getCustomColorStyles()
        : getPrimaryColorHierarchyStyles(),
      secondary: color ? getCustomColorStyles() : getSecondaryHierarchyStyles(),
      'secondary-color': color
        ? getCustomColorStyles()
        : getSecondaryColorHierarchyStyles(),
      tertiary: color ? getCustomColorStyles() : getTertiaryHierarchyStyles(),
      'tertiary-color': color
        ? getCustomColorStyles()
        : getTertiaryColorHierarchyStyles(),
      link: color ? getCustomColorStyles() : getLinkHierarchyStyles(),
      'link-color': color
        ? getCustomColorStyles()
        : getLinkColorHierarchyStyles(),
    }),
    [
      color,
      getCustomColorStyles,
      getLinkColorHierarchyStyles,
      getLinkHierarchyStyles,
      getPrimaryHierarchyStyles,
      getPrimaryColorHierarchyStyles,
      getSecondaryColorHierarchyStyles,
      getSecondaryHierarchyStyles,
      getTertiaryColorHierarchyStyles,
      getTertiaryHierarchyStyles,
    ],
  );

  const getCustomIconColorStyles = useCallback(() => {
    if (!color) return {};

    // For tertiary hierarchies, maintain transparent background style
    if (hierarchy === 'tertiary' || hierarchy === 'tertiary-color') {
      return {
        backgroundColor: 'inherit !important',
        boxShadow: ' none !important',
        color: `${color} !important`,
        '&:hover': {
          backgroundColor: `${color} !important`,
          color: `${common.white} !important`,
          opacity: 0.1,
        },
        '&:active': {
          backgroundColor: `${color} !important`,
          color: `${common.white} !important`,
          opacity: 0.2,
        },
        '&:disabled': getDisabledIconStyles(),
      };
    }

    return {
      backgroundColor: color,
      color: `${common.white} !important`,
      border: `1px solid ${color} !important`,
      '&:hover': {
        backgroundColor: `${color} !important`,
        color: common.white,
        border: `1px solid ${color} !important`,
        opacity: 0.8,
      },
      '&:active': {
        backgroundColor: `${color} !important`,
        color: common.white,
        border: `1px solid ${color} !important`,
        opacity: 0.9,
      },
      '&:disabled': getDisabledIconStyles(),
    };
  }, [color, common.white, getDisabledIconStyles, hierarchy]);

  const primaryIconStyles = useMemo(
    () => ({
      backgroundColor: destructive
        ? primary.error.bg.default
        : primary.default.bg.default,
      color: common.white,
      '&:hover': {
        backgroundColor: `${destructive ? primary.error.bg.hover : primary.default.bg.hover} !important`,
        color: common.white,
      },
      '&:active': {
        backgroundColor: `${destructive ? primary.error.bg.active : primary.default.bg.active} !important`,
        color: common.white,
      },
      '&:disabled': getDisabledIconStyles(),
    }),
    [common.white, destructive, getDisabledIconStyles, primary],
  );

  const primaryColorIconStyles = useMemo(
    () => ({
      backgroundColor: destructive
        ? primaryColor.error.bg.default
        : primaryColor.default.bg.default,
      color: common.white,
      '&:hover': {
        backgroundColor: `${destructive ? primaryColor.error.bg.hover : primaryColor.default.bg.hover} !important`,
        color: common.white,
      },
      '&:active': {
        backgroundColor: `${destructive ? primaryColor.error.bg.active : primaryColor.default.bg.active} !important`,
        color: common.white,
      },
      '&:disabled': getDisabledIconStyles(),
    }),
    [common.white, destructive, getDisabledIconStyles, primaryColor],
  );

  const secondaryIconStyles = useMemo(
    () => ({
      height: 'auto !important',
      minWidth: 'auto !important',
      width: 'auto !important',
      backgroundColor: destructive
        ? secondary.error.bg.default
        : secondary.default.bg.default,
      color: `${destructive ? secondary.error.fg.default : secondary.default.fg.default} !important`,
      border: `1px solid ${destructive ? secondary.error.border.default : secondary.default.border.default} !important`,
      boxShadow: theme.customShadows.dropShadowXS,
      padding: `${theme.sizeGuide.md} !important`,
      '&:hover': {
        color: `${destructive ? secondary.error.fg.hover : secondary.default.fg.hover} !important`,
        backgroundColor: destructive
          ? secondary.error.bg.hover
          : secondary.default.bg.hover,
        border: `1px solid ${destructive ? secondary.error.border.hover : secondary.default.border.hover} !important`,
        boxShadow: theme.customShadows.dropShadowXS,
      },
      '&:active': {
        backgroundColor: `${destructive ? secondary.error.bg.active : secondary.default.bg.active} !important`,
        border: `1px solid ${destructive ? secondary.error.border.active : secondary.default.border.active} !important`,
        color: `${destructive ? secondary.error.fg.active : secondary.default.fg.active} !important`,
        boxShadow: theme.customShadows.dropShadowXS,
      },
      '&:disabled': getDisabledIconStyles(),
    }),
    [destructive, getDisabledIconStyles, secondary, theme],
  );

  const secondaryColorIconStyles = useMemo(
    () => ({
      height: 'auto !important',
      minWidth: 'auto !important',
      width: 'auto !important',
      backgroundColor: destructive
        ? secondary.error.bg.default
        : secondary.default.bg.default,
      color: `${destructive ? secondary.error.fg.default : secondary.default.fg.color.default} !important`,
      border: `1px solid ${destructive ? secondary.error.border.default : secondary.default.border.color.default} !important`,
      boxShadow: theme.customShadows.dropShadowXS,
      padding: '4px !important',
      '&:hover': {
        color: `${destructive ? secondary.error.fg.hover : secondary.default.fg.color.hover} !important`,
        backgroundColor: destructive
          ? secondary.error.bg.hover
          : secondary.default.bg.color.hover,
        border: `1px solid ${destructive ? secondary.error.border.hover : secondary.default.border.color.hover} !important`,
        boxShadow: theme.customShadows.dropShadowXS,
      },
      '&:active': {
        backgroundColor: `${destructive ? secondary.error.bg.active : secondary.default.bg.color.active} !important`,
        border: `1px solid ${destructive ? secondary.error.border.active : secondary.default.border.color.active} !important`,
        color: `${destructive ? secondary.error.fg.active : secondary.default.fg.color.active} !important`,
        boxShadow: theme.customShadows.dropShadowXS,
      },
      '&:disabled': getDisabledIconStyles(),
    }),
    [destructive, getDisabledIconStyles, secondary, theme],
  );

  const tertiaryIconStyles = useMemo(
    () => ({
      backgroundColor: `inherit !important`,
      boxShadow: ' none !important',
      color: `${destructive ? tertiary.error.fg.default : tertiary.default.fg.default} !important`,
      '&:hover': {
        backgroundColor: `${destructive ? tertiary.error.bg.hover : tertiary.default.bg.hover} !important`,
        color: `${destructive ? tertiary.error.fg.hover : tertiary.default.fg.hover} !important`,
      },
      '&:active': {
        backgroundColor: `${destructive ? tertiary.error.bg.active : tertiary.default.bg.active} !important`,
        color: `${destructive ? tertiary.error.fg.active : tertiary.default.fg.active} !important`,
      },
      '&:disabled': getDisabledIconStyles(),
    }),
    [destructive, getDisabledIconStyles, tertiary],
  );

  const hierarchyIconStyles: { [key: string]: unknown } = useMemo(
    () => ({
      primary: color ? getCustomIconColorStyles() : primaryIconStyles,
      'primary-color': color
        ? getCustomIconColorStyles()
        : primaryColorIconStyles,
      secondary: color ? getCustomIconColorStyles() : secondaryIconStyles,
      'secondary-color': color
        ? getCustomIconColorStyles()
        : secondaryColorIconStyles,
      tertiary: color ? getCustomIconColorStyles() : tertiaryIconStyles,
    }),
    [
      color,
      getCustomIconColorStyles,
      primaryIconStyles,
      primaryColorIconStyles,
      secondaryColorIconStyles,
      tertiaryIconStyles,
      secondaryIconStyles,
    ],
  );

  const styles = useMemo(() => {
    return {
      button: {
        ...baseButtonStyles,
        ...(hierarchyStyles[hierarchy] as object),
        textTransform: 'none' as const,
      },
      circularProgress: {
        color: getProgressColor(),
        size: theme.sizeGuide['3xl'],
      },
      Icon: {
        ...baseIconStyles,
        ...(hierarchyIconStyles[hierarchy] as object),
        textTransform: 'none' as const,
      },
    };
  }, [
    baseButtonStyles,
    baseIconStyles,
    getProgressColor,
    hierarchy,
    hierarchyIconStyles,
    hierarchyStyles,
    theme,
  ]);

  return styles;
};

export default useAssetronButtonStyles;
